import React, { useState, useEffect } from 'react';
import { FaTimes, FaMapMarkerAlt, FaParking, FaKey, FaUser, FaPhone, FaEnvelope, FaStickyNote } from 'react-icons/fa';
import { toast } from 'react-toastify';
import PlantafelService, { ProjectSite, CreateSitePayload } from '../services/PlantafelService';

interface SiteModalProps {
  isOpen: boolean;
  site?: ProjectSite | null;
  projectId: number;
  onClose: () => void;
  onSave: () => void;
}

export default function SiteModal({
  isOpen,
  site,
  projectId,
  onClose,
  onSave,
}: SiteModalProps): React.JSX.Element | null {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [accessInfo, setAccessInfo] = useState('');
  const [parkingInfo, setParkingInfo] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const isEditMode = !!site;

  useEffect(() => {
    if (isOpen) {
      if (site) {
        setName(site.name || '');
        setAddress(site.address || '');
        setAccessInfo(site.access_info || '');
        setParkingInfo(site.parking_info || '');
        setContactName(site.contact_name || '');
        setContactPhone(site.contact_phone || '');
        setContactEmail(site.contact_email || '');
        setNotes(site.notes || '');
      } else {
        setName('');
        setAddress('');
        setAccessInfo('');
        setParkingInfo('');
        setContactName('');
        setContactPhone('');
        setContactEmail('');
        setNotes('');
      }
    }
  }, [isOpen, site]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error('Name ist erforderlich');
      return;
    }

    setSaving(true);
    try {
      const payload: CreateSitePayload = {
        name: name.trim(),
        address: address.trim() || undefined,
        access_info: accessInfo.trim() || undefined,
        parking_info: parkingInfo.trim() || undefined,
        contact_name: contactName.trim() || undefined,
        contact_phone: contactPhone.trim() || undefined,
        contact_email: contactEmail.trim() || undefined,
        notes: notes.trim() || undefined,
      };

      if (isEditMode && site) {
        await PlantafelService.updateProjectSite(site.id, payload);
        toast.success('Site aktualisiert');
      } else {
        await PlantafelService.createProjectSite(projectId, payload);
        toast.success('Site erstellt');
      }

      onSave();
      onClose();
    } catch (err: any) {
      console.error('Failed to save site:', err);
      toast.error(err?.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '600px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: '1px solid #e0e3ef',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#021a4a' }}>
            {isEditMode ? 'Site bearbeiten' : 'Neue Site erstellen'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: '#666',
            }}
          >
            <FaTimes size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          {/* Name */}
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>
              Name <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z.B. Hauptgebäude, Nebengebäude A"
              style={inputStyle}
              autoFocus
            />
          </div>

          {/* Address */}
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>
              <FaMapMarkerAlt size={12} style={{ marginRight: '6px' }} />
              Adresse
            </label>
            <textarea
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Straße, Hausnummer, PLZ, Stadt"
              style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
            />
          </div>

          {/* Access Info */}
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>
              <FaKey size={12} style={{ marginRight: '6px' }} />
              Zugang
            </label>
            <textarea
              value={accessInfo}
              onChange={(e) => setAccessInfo(e.target.value)}
              placeholder="Zugangsinformationen (Schlüssel, Code, Ansprechpartner vor Ort)"
              style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
            />
          </div>

          {/* Parking Info */}
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>
              <FaParking size={12} style={{ marginRight: '6px' }} />
              Parken
            </label>
            <textarea
              value={parkingInfo}
              onChange={(e) => setParkingInfo(e.target.value)}
              placeholder="Parkmöglichkeiten, Einfahrt, besondere Hinweise"
              style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
            />
          </div>

          {/* Contact Section */}
          <div style={{ background: '#f8f9fb', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#666', marginBottom: '12px' }}>
              Kontakt vor Ort
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ ...labelStyle, fontSize: '12px' }}>
                  <FaUser size={10} style={{ marginRight: '4px' }} />
                  Name
                </label>
                <input
                  type="text"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder="Kontaktperson"
                  style={{ ...inputStyle, padding: '8px 12px' }}
                />
              </div>
              <div>
                <label style={{ ...labelStyle, fontSize: '12px' }}>
                  <FaPhone size={10} style={{ marginRight: '4px' }} />
                  Telefon
                </label>
                <input
                  type="tel"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  placeholder="+49..."
                  style={{ ...inputStyle, padding: '8px 12px' }}
                />
              </div>
            </div>
            <div style={{ marginTop: '12px' }}>
              <label style={{ ...labelStyle, fontSize: '12px' }}>
                <FaEnvelope size={10} style={{ marginRight: '4px' }} />
                E-Mail
              </label>
              <input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="email@beispiel.de"
                style={{ ...inputStyle, padding: '8px 12px' }}
              />
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: '24px' }}>
            <label style={labelStyle}>
              <FaStickyNote size={12} style={{ marginRight: '6px' }} />
              Notizen
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Weitere Hinweise..."
              style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
            />
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '10px 20px',
                border: '1px solid #e0e3ef',
                borderRadius: '10px',
                background: 'white',
                color: '#666',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={saving || !name.trim()}
              style={{
                padding: '10px 24px',
                border: 'none',
                borderRadius: '10px',
                background: saving || !name.trim() ? '#ccc' : '#021a4a',
                color: 'white',
                fontSize: '14px',
                fontWeight: 600,
                cursor: saving || !name.trim() ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Speichern...' : isEditMode ? 'Speichern' : 'Erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  fontSize: '13px',
  fontWeight: 600,
  color: '#333',
  marginBottom: '6px',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 14px',
  border: '1px solid #e0e3ef',
  borderRadius: '6px',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box',
};
